import { BUCHAREST_SECTORS, RO_COUNTIES, isValidCui } from '@financiar/shared';
import { Form, Input, InputNumber, Select, Switch, type FormInstance } from 'antd';
import { normalizeCui } from '../../lib/forms';
import { MoneyInput } from '../../lib/format';

const COUNTY_OPTIONS = RO_COUNTIES.map((county) => ({ value: county.code, label: county.name }));
const SECTOR_OPTIONS = BUCHAREST_SECTORS.map((sector) => ({
  value: sector,
  label: `Sector ${sector.replace('SECTOR', '')}`,
}));

/** Baza estimării de profit net din dashboard (widgetul Profit). */
export const TAX_REGIME_OPTIONS = [
  { value: 'MICRO_1', label: 'Microîntreprindere — 1% pe venit' },
  { value: 'MICRO_3', label: 'Microîntreprindere — 3% pe venit' },
  { value: 'PROFIT_16', label: 'Impozit pe profit — 16%' },
];

/**
 * The base "Date firmă" fields — shared between the plain create form and
 * the "Date firmă" tab shown at edit. Must be rendered as a descendant of
 * the `<Form>` that owns `form` (relies on AntD's Form context for
 * `Form.Item`/`Form.useWatch`, not on `form` being passed down any further).
 */
export function FirmaDateFields({ form }: { form: FormInstance }) {
  const countyCode = Form.useWatch('countyCode', form);

  return (
    <>
      <Form.Item name="name" label="Denumire" rules={[{ required: true, message: 'Denumirea este obligatorie.' }]}>
        <Input />
      </Form.Item>
      <Form.Item
        name="cui"
        label="CUI"
        rules={[
          { required: true, message: 'CUI este obligatoriu.' },
          {
            validator: (_, value: string) =>
              isValidCui(normalizeCui(value)) ? Promise.resolve() : Promise.reject(new Error('CUI invalid.')),
          },
        ]}
      >
        <Input placeholder="RO12345678 sau 12345678" />
      </Form.Item>
      <Form.Item name="regCom" label="Reg. Com.">
        <Input placeholder="J40/1234/2024" />
      </Form.Item>
      <Form.Item name="addressLine" label="Adresă" rules={[{ required: true, message: 'Adresa este obligatorie.' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="countyCode" label="Județ" rules={[{ required: true, message: 'Județul este obligatoriu.' }]}>
        <Select
          options={COUNTY_OPTIONS}
          showSearch
          optionFilterProp="label"
          onChange={() => form.setFieldValue('cityName', undefined)}
        />
      </Form.Item>
      <Form.Item name="cityName" label="Localitate" rules={[{ required: true, message: 'Localitatea este obligatorie.' }]}>
        {countyCode === 'RO-B' ? <Select options={SECTOR_OPTIONS} /> : <Input />}
      </Form.Item>
      <Form.Item name="postalCode" label="Cod poștal">
        <Input />
      </Form.Item>
      <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email invalid.' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="phone" label="Telefon">
        <Input />
      </Form.Item>
      <Form.Item name="capitalSocial" label="Capital social">
        <MoneyInput />
      </Form.Item>
      <Form.Item
        name="defaultDueDays"
        label="Zile scadență implicite"
        rules={[{ required: true, message: 'Zilele de scadență sunt obligatorii.' }]}
      >
        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="defaultInvoiceNotes" label="Note implicite factură">
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item name="taxRegime" label="Regim fiscal" rules={[{ required: true, message: 'Regimul fiscal este obligatoriu.' }]}>
        <Select options={TAX_REGIME_OPTIONS} />
      </Form.Item>
      <Form.Item name="isActive" label="Activă" valuePropName="checked">
        <Switch />
      </Form.Item>
    </>
  );
}
